/**
 * Reusable Mantine modal for creating or editing a device through the
 * /v1/admin/devices endpoints (§9.3 of DEVELOPER_CONTINUATION_GUIDE).
 *
 * Mirrors `MeterFormModal` so the two admin pages feel identical:
 *   - same single-component "create or edit" shape,
 *   - same SLUG_PATTERN + immutable `(site_slug, slug)` contract,
 *   - same `device_conflict` (409) inline alert with actionable copy.
 *
 * Differences from the meter modal:
 *   - no `unit` field (the unit lives on the channels, not the device),
 *   - no `device_slug` (devices don't nest under devices),
 *   - status is `{active, retired}` only (no `commissioning`).
 */
import {
  Alert,
  Autocomplete,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { useCreateDevice, useUpdateDevice } from "@/api/queries";
import {
  DEVICE_KIND_SUGGESTIONS,
  DEVICE_STATUSES,
  type AdminDevice,
  type AdminDeviceStatus,
} from "@/api/types";
import { SiteSelect } from "@/components/SiteSelect";

type CreateDefaults = {
  site_slug?: string;
};

export type DeviceFormModalProps = {
  opened: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  device?: AdminDevice;
  /** Pre-fills site_slug in create mode (used by the per-site flow if/when added). */
  defaults?: CreateDefaults;
  onSuccess?: (device: AdminDevice) => void;
};

type FormValues = {
  site_slug: string;
  slug: string;
  display_name: string;
  kind: string;
  status: AdminDeviceStatus;
};

type FieldErrors = Partial<Record<keyof FormValues, string>>;

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/u;
const SLUG_HINT = "lowercase slug, [a-z0-9_-], must start and end with alphanumeric";

function defaultValues(
  mode: DeviceFormModalProps["mode"],
  device: AdminDevice | undefined,
  defaults: CreateDefaults | undefined,
): FormValues {
  if (mode === "edit" && device) {
    return {
      site_slug: device.site_slug,
      slug: device.slug,
      display_name: device.display_name,
      kind: device.kind,
      status: (DEVICE_STATUSES as readonly string[]).includes(device.status)
        ? (device.status as AdminDeviceStatus)
        : "active",
    };
  }
  return {
    site_slug: defaults?.site_slug ?? "",
    slug: "",
    display_name: "",
    kind: "",
    status: "active",
  };
}

function validate(values: FormValues, mode: "create" | "edit"): FieldErrors {
  const errors: FieldErrors = {};
  if (mode === "create") {
    // site_slug comes from SiteSelect, so it's either a valid slug or "".
    // Distinguish "you forgot to pick" from "your slug is malformed" so
    // the inline error makes sense for each surface.
    if (values.site_slug.length === 0) {
      errors.site_slug = "required";
    } else if (!SLUG_PATTERN.test(values.site_slug)) {
      errors.site_slug = SLUG_HINT;
    }
    if (!SLUG_PATTERN.test(values.slug)) errors.slug = SLUG_HINT;
  }
  if (values.display_name.trim().length === 0) errors.display_name = "required";
  if (values.kind.trim().length === 0) errors.kind = "required";
  return errors;
}

export function DeviceFormModal({
  opened,
  onClose,
  mode,
  device,
  defaults,
  onSuccess,
}: DeviceFormModalProps) {
  const createMutation = useCreateDevice();
  const updateMutation = useUpdateDevice();
  const mutation = mode === "create" ? createMutation : updateMutation;

  const [values, setValues] = useState<FormValues>(() => defaultValues(mode, device, defaults));
  const [errors, setErrors] = useState<FieldErrors>({});

  // Reset whenever the modal is reopened against a different target.
  // Without this, opening Edit on device A then device B would keep A's
  // values until the user clicked into a field.
  useEffect(() => {
    if (opened) {
      setValues(defaultValues(mode, device, defaults));
      setErrors({});
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, mode, device?.id]);

  const set = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fieldErrors = validate(values, mode);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    try {
      if (mode === "create") {
        const created = await createMutation.mutateAsync({
          site_slug: values.site_slug,
          slug: values.slug,
          display_name: values.display_name,
          kind: values.kind,
          status: values.status,
        });
        onSuccess?.(created);
      } else if (device) {
        const updated = await updateMutation.mutateAsync({
          id: device.id,
          body: {
            display_name: values.display_name,
            kind: values.kind,
            status: values.status,
          },
        });
        onSuccess?.(updated);
      }
      onClose();
    } catch {
      // Error is surfaced via `mutation.error` and rendered inline below.
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === "create" ? "Create device" : `Edit ${device?.display_name ?? "device"}`}
      centered
      size="md"
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack>
          {mutation.error && <FormErrorAlert error={mutation.error} />}

          {mode === "create" && (
            <>
              <SiteSelect
                label="Site"
                description="Site that owns this device (immutable)."
                required
                value={values.site_slug || null}
                onChange={(v) => set("site_slug", v ?? "")}
                error={errors.site_slug}
              />
              <TextInput
                label="Device slug"
                description="Lowercase identifier, unique within the site."
                required
                value={values.slug}
                onChange={(e) => set("slug", e.currentTarget.value)}
                error={errors.slug}
              />
            </>
          )}

          {mode === "edit" && device && (
            <TextInput
              label="Slug"
              description="Slug and site are immutable — re-create the device to change them."
              value={`${device.site_slug} / ${device.slug}`}
              readOnly
              variant="filled"
            />
          )}

          <TextInput
            label="Display name"
            required
            value={values.display_name}
            onChange={(e) => set("display_name", e.currentTarget.value)}
            error={errors.display_name}
          />

          <Autocomplete
            label="Kind"
            required
            data={[...DEVICE_KIND_SUGGESTIONS]}
            description="Suggested values; any free-form string is accepted."
            value={values.kind}
            onChange={(value) => set("kind", value)}
            error={errors.kind}
          />

          <Select
            label="Status"
            data={DEVICE_STATUSES.map((s) => ({ value: s, label: s }))}
            allowDeselect={false}
            value={values.status}
            onChange={(value) => {
              if (value && (DEVICE_STATUSES as readonly string[]).includes(value)) {
                set("status", value as AdminDeviceStatus);
              }
            }}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {mode === "create" ? "Create" : "Save changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/**
 * Friendly inline error renderer. We treat `device_conflict` (slug
 * clash) separately because it has actionable guidance; everything
 * else falls back to the standard `code: message` shape.
 */
function FormErrorAlert({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "device_conflict") {
    return (
      <Alert
        icon={<IconAlertCircle size={18} />}
        color="orange"
        variant="light"
        title="Slug already in use"
      >
        A device with that slug already exists in this site. Pick a different
        slug or edit the existing one.
      </Alert>
    );
  }
  const message =
    error instanceof ApiError
      ? `${error.code}: ${error.message}`
      : error instanceof Error
        ? error.message
        : "Unknown error";
  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" variant="light" title="Save failed">
      {message}
    </Alert>
  );
}
